// src/pages/admin/emails/components/SendDetailDialog.tsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../../lib/supabaseClient";

export default function SendDetailDialog({
  row,
  onClose,
}: {
  row: any;
  onClose: () => void;
}) {
  const [events, setEvents] = useState<any[]>([]);
  const [links, setLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: ev }, { data: ln }] = await Promise.all([
        supabase
          .from("email_metrics")
          .select("id, event, meta, created_at")
          .eq("send_id", row.id)
          .order("created_at", { ascending: true }),
        supabase
          .from("email_links")
          .select("id, label, target_url, token, created_at")
          .eq("send_id", row.id)
          .order("created_at", { ascending: true }),
      ]);
      setEvents(ev ?? []);
      setLinks(ln ?? []);
      setLoading(false);
    })();
  }, [row.id]);

  const summary = useMemo(() => {
    const s: Record<string, number> = {};
    for (const e of events) {
      s[e.event] = (s[e.event] ?? 0) + 1;
    }
    return s;
  }, [events]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#161b27] border border-[#2a3347] w-full max-w-3xl rounded-xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a3347]">
          <h3 className="font-semibold text-lg text-slate-100">
            Versand-Details – {row.email_templates?.name ?? row.template_id}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">✕</button>
        </div>

        {loading ? (
          <div className="p-6 text-slate-400">Lade…</div>
        ) : (
          <div className="p-4 space-y-4 text-sm">
            <section className="grid grid-cols-2 gap-4">
              <Field label="Empfänger" value={`${row.profiles?.email ?? "—"} (${row.user_id})`} />
              <Field label="Provider" value={`${row.provider}${row.provider_message_id ? ` • ${row.provider_message_id}` : ""}`} />
              <Field label="Gesendet" value={row.sent_at ? new Date(row.sent_at).toLocaleString() : "—"} />
              <Field label="Fehlgeschlagen" value={row.failed_at ? new Date(row.failed_at).toLocaleString() : "—"} />
            </section>

            {row.error && (
              <section>
                <div className="text-slate-400 mb-1">Fehler</div>
                <pre className="bg-red-950/20 border border-red-800/30 rounded p-2 whitespace-pre-wrap text-red-400">
                  {row.error}
                </pre>
              </section>
            )}

            <section>
              <div className="text-slate-400 mb-1">Events</div>
              {events.length === 0 ? (
                <div className="p-3 bg-[#1e2639] border border-[#2a3347] rounded text-slate-500">Keine Events vorhanden.</div>
              ) : (
                <ul className="space-y-2">
                  {events.map((e) => (
                    <li key={e.id} className="flex items-start justify-between gap-3 border border-[#2a3347] rounded p-2 bg-[#1e2639]">
                      <div>
                        <div className="font-medium text-slate-100">{e.event}</div>
                        {e.meta && Object.keys(e.meta || {}).length > 0 && (
                          <pre className="bg-[#0d1117] border border-[#2a3347] rounded p-2 mt-1 text-xs whitespace-pre-wrap text-slate-300">
                            {JSON.stringify(e.meta, null, 2)}
                          </pre>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-2 text-xs text-slate-500">
                Zusammenfassung: delivered {summary["delivered"] ?? 0} • opened {summary["opened"] ?? 0} • clicked {summary["clicked"] ?? 0} • bounced {summary["bounced"] ?? 0} • complained {summary["complained"] ?? 0}
              </div>
            </section>

            <section>
              <div className="text-slate-400 mb-1">Links (getrackt)</div>
              {links.length === 0 ? (
                <div className="p-3 bg-[#1e2639] border border-[#2a3347] rounded text-slate-500">Keine Links registriert.</div>
              ) : (
                <ul className="space-y-2">
                  {links.map((l) => (
                    <li key={l.id} className="border border-[#2a3347] rounded p-2 bg-[#1e2639]">
                      <div className="font-mono text-xs break-all text-slate-300">{l.target_url}</div>
                      <div className="text-xs text-slate-500">
                        Label: {l.label ?? "—"} • Token: <code className="text-sky-400">{l.token}</code> • {new Date(l.created_at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}

        <div className="px-4 py-3 border-t border-[#2a3347] flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-slate-400">{label}</div>
      <div className="font-medium break-words text-slate-100">{value}</div>
    </div>
  );
}
